// @ts-nocheck
import React, { useEffect } from "react";
import {
  Box,
  Grid,
  Container,
  Badge,
  styled,
  Skeleton,
} from "@mui/material";

import "reactjs-popup/dist/index.css";
import useAppContextProvider from "../AppContext/useAppContextProvider.js";

import { useHistory, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
  BodyLarge,
  H7TitleLarge,
} from "../components/StyledComponents/Typography/Typography.tsx";

import { useQuery } from "@tanstack/react-query";
import { usePostOrg } from "../api/queries/usePostOrg.ts"; // Adjust the path if necessary
import { GetUserUseQuery } from "../api/api.js";
import { PriceConfigurationHeader } from "./Components/PriceConfigurationHeader/PriceConfigurationHeader.js";

import PriceConfigurationCard from "./Components/PriceConfigurationCard/PriceConfigurationCard.js";
import usePriceConfigurationContext from "./context/usePriceConfigurationContext.js";
import { isEmpty } from "lodash";
import PageWrapper from "../components/PageWrapper/PageWrapper.js";
import { useTheme } from "../ThemeContext/ThemeContext.js";
const CustomBadge = styled(Badge)(({ colors }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: colors?.text?.primary,   
    color: colors?.primary?.contrastText,
    fontSize: "0.75rem", // Optional: Adjust font size
    padding: "0.25rem", // Optional: Adjust padding
  },
}));

export const HeaderContainer = React.memo((props) => {
  const { data, totalCount } = props;
  const history = useHistory();
  const { colors } = useTheme();
  return (
    <Box
      className={"header-container"}
      display={"flex"}
      justifyContent="space-between"
      flexDirection="column"
      gap={4}
      marginBottom={"1.5rem"}
    >
      <Box
        className={"title-box"}
        display={"flex"}
        flexDirection={"column"}
        justifyContent={"space-between"}
        gap={3.5}
      >
        {totalCount && totalCount !== 0 ? (
          <Box
            display={"flex"}
            flexDirection={"row"}
            justifyContent={"space-between"}
          >
            <CustomBadge
              colors={colors}
              badgeContent={totalCount}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              sx={{}}
            >
              <Box pr={3}>
                <H7TitleLarge>
                  Price Configuration
                </H7TitleLarge>
              </Box>
            </CustomBadge>
          </Box>
        ) : (
          <H7TitleLarge>Price Configuration</H7TitleLarge>
        )}
        <BodyLarge>
          Manage price configurations to control costs and
          optimize profitability. View the number of sites that
          are linked to each configuration.
        </BodyLarge>
      </Box>
    </Box>
  );
});

/**
 * Represents the MinerPage component.
 * This component displays a table of miner devices and provides functionality to interact with them.
 */
export default function PriceConfigurationPage() {
  const history = useHistory();
  const location = useLocation();
  const { colors } = useTheme();
  const [totalCount, setTotalCount] = React.useState(null);
  const {
    setPriceConfigData,
    setPriceConfigDataCopy,
    priceConfigData: priceConfigDataState,
  } = usePriceConfigurationContext();

  const limit = 25;

  const [authToken, setAuthToken] = React.useState("");

  const { getAccessTokenSilently, isAuthenticated } =
    useAuth0();

  useEffect(() => {
    const fetchToken = async () => {
      try {
        if (!isAuthenticated) {
          setAuthToken("");
          console.warn(
            "User is not authenticated. Fetching token..."
          );
          return;
        }
        const token = await getAccessTokenSilently({
          cacheMode: "cache-only",
          ignoreCache: true,
        });
        setAuthToken(token);
      } catch (error) {
        console.error(" Error fetching token:", error);
      }
    };
    fetchToken();
    const interval = setInterval(() => {
      fetchToken();
    }, 5 * 60 * 1000); // Refresh every 15 minutes

    return () => clearInterval(interval); // Cleanup on unmount
  }, [getAccessTokenSilently, isAuthenticated]);

  const {
    data: userInfo,
    isFetched: isUserInfoFetched,
    isFetching: isUserInfoFetching,
  } = useQuery({
    queryKey: ["userInfo"],
    queryFn: () => GetUserUseQuery(authToken),
    enabled: !!authToken,
  });
  const user = userInfo?.user;
  const userRole = user?.role;

  const projection = {
    priceConfig: 1,
  };

  const {
    data: priceConfigData,
    refetch: priceConfigRefetch,
    isLoading: isPriceConfigLoading,
    isFetching: isPriceConfigFetching,
    isFetched: isPriceConfigFetched,
    isError: isPriceConfigError,
    error: priceConfigError,
    isRefetching: isPriceConfigRefetching,
  } = usePostOrg({
    open: true,
    authToken,
    ...{
      projection: projection,
    },
  });

  useEffect(() => {
    const priceConfig =
      priceConfigData && priceConfigData?.result
        ? priceConfigData?.result?.priceConfig
        : {};
    const actualPriceConfig =
      !isEmpty(priceConfig) &&
      Object.keys(priceConfig).length > 0
        ? Object.entries(priceConfig).map(
            ([key, value]) => ({
              ...value,
              priceConfigName: key,
            })
          )
        : [];
    setPriceConfigData(actualPriceConfig);
    setPriceConfigDataCopy(actualPriceConfig);
    const count = priceConfigData?.result?.priceConfig
      ? Object.keys(priceConfigData.result.priceConfig)
          .length
      : 0;
    setTotalCount(count);
  }, [priceConfigData, isPriceConfigFetched]);

  return (
    //@ts-nocheck
    <PageWrapper>
      <HeaderContainer totalCount={totalCount} />
      <Grid
        container
        className={"miner-page-table-container"}
        display={"flex"}
        flexDirection={"row"}
        spacing={4}
        sx={{ transition: "all 0.3s ease-in-out" }}
      >
        <Grid
          item
          xs={12}
          sx={{
            transition: "width 0.3s ease-in-out", // Smooth transition for width
          }}
        >
          <Box
            className={"miner-page-table"}
            display={"flex"}
            flexDirection={"row"}
            backgroundColor={colors.background.paper}
            padding={"0.75rem 0  0.75rem 0"}
            borderRadius={"1rem"}
            sx={{
              transition: "width 0.3s ease-in-out", // Smooth transition for width
            }}
          >
            <Box
              className={"table-container"}
              display={"flex"}
              flexDirection={"column"}
              padding={"0 0.75rem 0 0.75rem"}
              height={"auto"}
              overflow={"auto"}
              minWidth={"0"}
              gap={4}
              flex={3}
              flexShrink={1}
            >
              {isPriceConfigFetched &&
                priceConfigData.status &&
                priceConfigData.status === "ok" && (
                  <PriceConfigurationHeader
                    refetch={priceConfigRefetch}
                    userRole={userRole}
                    totalCount={totalCount}
                    data={priceConfigData}
                  />
                )}
              {(isPriceConfigFetching ||
                isPriceConfigLoading) && (
                <Box
                  className="table-container-header"
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                  minHeight="4rem"
                >
                  <Skeleton
                    variant="rectangular"
                    width={350}
                    height={40}
                    sx={{ borderRadius: "1.75rem" }}
                  />
                  <Skeleton
                    variant="rectangular"
                    width={120}
                    height={40}
                    sx={{ borderRadius: "2rem" }}
                  />
                </Box>
              )}
              <Box
                className={"Datagrid-container"}
                display={"flex"}
                flexShrink={1}
                flexGrow={1}
                sx={{
                  overflowX: "auto",
                  scrollbarWidth: "thin",
                  scrollBehavior: "smooth",
                }}
                height="calc(100vh - 17rem)"
              >
                <PriceConfigurationCard
                  refetch={priceConfigRefetch}
                  isLoading={isPriceConfigLoading}
                  isFetched={isPriceConfigFetched}
                  isFetching={isPriceConfigFetching}
                  isError={isPriceConfigError}
                  error={priceConfigError}
                  status={priceConfigData?.status}
                  userRole={userRole}
                  data={
                    priceConfigData?.result?.priceConfig ||
                    {}
                  }
                />
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </PageWrapper>
  );
}
